import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Heart, Minus, Plus, Star, ArrowLeft } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import ProductCard from "@/components/ProductCard";

export default function ProductDetailPage() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [related, setRelated] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const { add } = useCart();
  const { has, toggle } = useWishlist();

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      setActiveImg(0);
      api.get("/products", { params: { subcategory: r.data.subcategory, limit: 4 } })
        .then((rr) => setRelated(rr.data.filter((x) => x.id !== r.data.id).slice(0, 4)))
        .catch(() => {});
    }).catch(() => setProduct(false));
  }, [slug]);

  if (product === null) return <div className="max-w-7xl mx-auto px-6 py-24">Loading…</div>;
  if (product === false) return <div className="max-w-7xl mx-auto px-6 py-24">Product not found. <Link to="/shop" className="text-electric">Back to shop</Link></div>;

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-8">
      <Link to="/shop" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6"><ArrowLeft className="w-4 h-4" /> Back</Link>
      <div className="grid md:grid-cols-2 gap-10">
        <div className="space-y-3">
          <div className="aspect-square rounded-2xl bg-secondary/60 overflow-hidden border border-border">
            {product.images?.[activeImg] && <img src={product.images[activeImg]} alt={product.name} className="w-full h-full object-cover" />}
          </div>
          {product.images?.length > 1 && (
            <div className="grid grid-cols-5 gap-2">
              {product.images.map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`aspect-square rounded-lg overflow-hidden border-2 transition-colors ${activeImg === i ? "border-electric" : "border-transparent"}`}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">{product.subcategory.replace(/-/g, " ")}</div>
          <h1 className="mt-2 font-heading font-medium text-3xl sm:text-5xl tracking-tight">{product.name}</h1>
          <div className="mt-4 flex items-center gap-3">
            <div className="flex gap-0.5 text-electric">
              {Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`w-4 h-4 ${i < Math.round(product.rating) ? "fill-current" : ""}`} />)}
            </div>
            <div className="text-sm text-muted-foreground">{product.rating} · {product.reviews_count} reviews</div>
          </div>
          <div className="mt-6 flex items-baseline gap-3">
            <div className="text-3xl font-heading font-medium">₹{product.price.toLocaleString()}</div>
            {product.compare_price && <div className="text-lg text-muted-foreground line-through">₹{product.compare_price.toLocaleString()}</div>}
          </div>
          <p className="mt-6 text-muted-foreground leading-relaxed">{product.description}</p>

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center gap-1 border border-border rounded-full p-1">
              <button data-testid="pd-qty-dec" onClick={() => setQty(Math.max(1, qty - 1))} className="w-9 h-9 rounded-full hover:bg-secondary flex items-center justify-center"><Minus className="w-3.5 h-3.5" /></button>
              <div className="w-8 text-center text-sm tabular-nums">{qty}</div>
              <button data-testid="pd-qty-inc" onClick={() => setQty(qty + 1)} className="w-9 h-9 rounded-full hover:bg-secondary flex items-center justify-center"><Plus className="w-3.5 h-3.5" /></button>
            </div>
            <button data-testid="pd-add-cart" onClick={() => add(product, qty)} className="flex-1 h-11 rounded-full bg-electric text-white font-medium hover:bg-electric-600 transition-colors">Add to cart · ₹{(product.price * qty).toLocaleString()}</button>
            <button data-testid="pd-wishlist" onClick={() => toggle(product)} className={`w-11 h-11 rounded-full border flex items-center justify-center transition-colors ${has(product.id) ? "border-electric text-electric" : "border-border hover:border-foreground"}`}>
              <Heart className={`w-4 h-4 ${has(product.id) ? "fill-current" : ""}`} />
            </button>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-3 text-xs">
            {[
              ["Warranty", "2 Years"],
              ["Shipping", "Free ₹999+"],
              ["Returns", "30 Days"],
            ].map(([k, v]) => (
              <div key={k} className="p-3 rounded-xl border border-border">
                <div className="font-mono uppercase tracking-[0.2em] text-muted-foreground">{k}</div>
                <div className="mt-1 font-medium">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <div className="mt-24">
          <h2 className="font-heading font-medium text-2xl mb-6">You may also like</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {related.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      )}
    </div>
  );
}
